import { useSnackbarStack } from '@superdispatch/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogout } from 'shared/auth/data/AuthAPI';
import { useShowSubscriptionCancelledDialog } from 'shared/errors/data/useShowSubscriptionCancelledDialog';
import { useShowTrialExpiredDialog } from 'shared/errors/data/useShowTrialExpiredDialog';
import { clearHeapCarrierGuid } from 'shared/helpers/Analytics';
import { clearUser } from 'shared/helpers/Logger';
import { SplashScreen } from 'shared/layout/SplashScreen';
import { DISPATCHER_PROFILE_CACHE_KEY } from 'shared/modules/dispatcher/DispatcherAPI';
import { deleteDispatcherProfileCache } from 'shared/modules/dispatcher/dispatcherProfileStorage';
import { DispatcherDTO } from 'shared/modules/dispatcher/DispatcherDTO';
import { deleteOnboardingStepCache } from 'shared/modules/onboarding/data/onboardingStepStorage';
import { userPermissionStore } from 'shared/modules/permission/PermissionAPI';
import { useSubscriptionCelebrationState } from 'shared/modules/subscription/core/useSubscriptionCelebrationState';
import { billingAddressStore } from 'shared/modules/subscription/SubscriptionAPI';
import { clearAuthToken, saveAuthRole } from './AuthToken';
import { useVerification } from './core/VerificationStorage';
import { useDriverAccount } from './data/useDriverAccount';

export function LogoutContainerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { removeVerification } = useVerification();
  const { removeDriverToken } = useDriverAccount();
  const { removeTrialExpiredDialog } = useShowTrialExpiredDialog();
  const { removeSubscriptionCancelledDialog } =
    useShowSubscriptionCancelledDialog();
  const { removeStatuses } = useSubscriptionCelebrationState();

  const { addSnackbar } = useSnackbarStack();
  const { mutate } = useLogout({
    onSuccess() {
      const profile = queryClient.getQueryData<DispatcherDTO>(
        DISPATCHER_PROFILE_CACHE_KEY,
      );
      const guid = profile?.guid;

      clearUser();
      if (guid) {
        deleteDispatcherProfileCache(guid);
      }
      deleteOnboardingStepCache(guid);
      queryClient.clear();
      clearAuthToken();
      clearHeapCarrierGuid();
      userPermissionStore.delete();
      saveAuthRole(null);
      billingAddressStore.delete();
      removeVerification();
      removeTrialExpiredDialog();
      removeSubscriptionCancelledDialog();
      removeDriverToken();
      removeStatuses();
      navigate('/login', { replace: true });
    },
    onError() {
      addSnackbar('Failed to logout, please contact support', {
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    mutate();
  }, [mutate]);

  return <SplashScreen />;
}
